import { boot } from 'quasar/wrappers';
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $axios: AxiosInstance;
    $api: AxiosInstance;
  }
}

// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)
const api = axios.create({
  baseURL: process.env.API_BASE_URL,
  withCredentials: true,
  withXSRFToken: true,
});

const apiMultipart = axios.create({
  baseURL: process.env.API_BASE_URL,
  withCredentials: true,
  withXSRFToken: true,
});

// Separate client without interceptors to fetch CSRF cookie safely (avoid recursion)
const csrfClient = axios.create({
  baseURL: process.env.API_BASE_URL,
  withCredentials: true,
});

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(';').shift() || null;
  return null;
}

async function ensureCsrfCookie(): Promise<void> {
  const xsrf = getCookie('XSRF-TOKEN');
  if (!xsrf) {
    await csrfClient.get('sanctum/csrf-cookie');
  }
}

const AUTH_ENDPOINTS = ['login', 'logout', 'login-as', 'sanctum/csrf-cookie'];
const IDLE_TIMEOUT = 5000;

let inFlight = 0;
let idleWaiters: Array<() => void> = [];
let pendingSwitch: Promise<unknown> | null = null;

function isAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  const path = url.replace(/^\/+/, '');
  return AUTH_ENDPOINTS.some((endpoint) => path.startsWith(endpoint));
}

function requestStarted(): void {
  inFlight += 1;
}

function requestFinished(): void {
  inFlight = Math.max(0, inFlight - 1);
  if (inFlight === 0) {
    const waiters = idleWaiters;
    idleWaiters = [];
    waiters.forEach((resolve) => resolve());
  }
}

function waitUntilIdle(): Promise<void> {
  if (inFlight === 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    idleWaiters.push(done);
    setTimeout(done, IDLE_TIMEOUT);
  });
}

async function awaitPendingSwitch(): Promise<void> {
  while (pendingSwitch) {
    await pendingSwitch.catch(() => undefined);
  }
}

export async function withSessionSwitch<T>(
  switchSession: () => Promise<T>,
): Promise<T> {
  await awaitPendingSwitch();
  await waitUntilIdle();

  const running = switchSession();
  pendingSwitch = running;
  try {
    return await running;
  } finally {
    if (pendingSwitch === running) pendingSwitch = null;
  }
}

type SessionRecovery = () => Promise<boolean>;

let sessionRecovery: SessionRecovery | null = null;
let runningRecovery: Promise<boolean> | null = null;

export function setSessionRecovery(recovery: SessionRecovery): void {
  sessionRecovery = recovery;
}

function recoverSession(): Promise<boolean> {
  if (!sessionRecovery) return Promise.resolve(false);
  if (!runningRecovery) {
    runningRecovery = sessionRecovery()
      .catch(() => false)
      .finally(() => {
        runningRecovery = null;
      });
  }
  return runningRecovery;
}

function trackRequests(instance: AxiosInstance, gated: boolean): void {
  instance.interceptors.request.use(async (config) => {
    if (gated) {
      if (!isAuthEndpoint(config.url)) {
        await awaitPendingSwitch();
      }
      await ensureCsrfCookie();
    }
    requestStarted();
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      requestFinished();
      return response;
    },
    (error) => {
      requestFinished();
      return Promise.reject(error);
    },
  );
}

trackRequests(api, true);
trackRequests(apiMultipart, true);
trackRequests(csrfClient, false);

function retryOnSessionError(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => {
      // If the response is successful (status code 2xx), return the response data
      return response;
    },
    async (error) => {
      const failedRequest = error.config as
        | (InternalAxiosRequestConfig & {
            _retry?: boolean;
            _sessionRetry?: boolean;
          })
        | undefined;

      if (
        error.response?.status === 419 &&
        failedRequest &&
        !failedRequest._retry
      ) {
        failedRequest._retry = true;

        await csrfClient.get('sanctum/csrf-cookie');
        return instance(failedRequest);
      }

      if (
        error.response?.status === 403 &&
        failedRequest &&
        !failedRequest._sessionRetry
      ) {
        failedRequest._sessionRetry = true;

        if (await recoverSession()) {
          return instance(failedRequest);
        }
      }
      // handle any other error normally
      return Promise.reject(error);
    },
  );
}

retryOnSessionError(api);
retryOnSessionError(apiMultipart);

export default boot(({ app, router }) => {
  app.config.globalProperties.$axios = axios;
  app.config.globalProperties.$api = api;

  // Axios response interceptor
  api.interceptors.response.use(
    (response) => {
      // Return the response if it's successful
      return response;
    },
    (error) => {
      // Check if the error response is a 401
      if (error.response && error.response.status === 401) {
        // Redirect to login page
        router.push({ name: 'login' });
      }
      // Return the error to allow further handling
      return Promise.reject(error);
    },
  );
});

export { api, apiMultipart };
